import React, { useState } from 'react'
import { connect } from 'react-redux'
import EmailVerificationEmailPasswordForm from '../../../components/modals/emailVerification/EmailVerificationEmailPasswordForm'
import EmailVerificationCodeEntry from '../../../components/modals/emailVerification/EmailVerificationCodeEntry'
import {
  createEmailVerification, resendEmailVerification, verifyEmailVerification,
} from '../../../actions/emailVerification'
import { showNotification } from '../../../actions/notifications'
import getErrorMessageForRequest from '../../../utils/getErrorMessageForRequest'

// Simple, standalone "update account email" flow for the account page — just
// email (+ password if the session needs re-authing) -> code -> done. This is
// intentionally separate from the bigger email-verification prompt modal.
function UpdateEmailContainer(props) {
  const {
    user, hideModal, createEmailVerification, resendEmailVerification, verifyEmailVerification,
    showNotification,
  } = props

  const [step, setStep] = useState('form') // 'form' | 'code'
  const [verification, setVerification] = useState(null) // { id, email, resendAvailableAt }
  const [loading, setLoading] = useState(false)
  const [needsPassword, setNeedsPassword] = useState(false)
  const [formError, setFormError] = useState('')

  const handleSubmit = async ({ email, password }) => {
    setLoading(true)
    setFormError('')
    try {
      const response = await createEmailVerification(user.id, { purpose: 'updatePrimary', email, password })
      setLoading(false)
      setNeedsPassword(false)
      setVerification({ id: response.id, email, resendAvailableAt: response.resendAvailableAt })
      setStep('code')
    } catch (err) {
      setLoading(false)
      if (err.response && err.response.status === 403) {
        setNeedsPassword(true)
        setFormError('Please confirm your password to continue.')
        return
      }
      setFormError(getErrorMessageForRequest({ requestName: 'createEmailVerification', response: err.response, data: { email } }))
    }
  }

  const handleVerifyCode = async (code) => {
    try {
      await verifyEmailVerification(user.id, verification.id, code, 'updatePrimary')
      hideModal()
      showNotification('', 'Account email updated', 'default')
    } catch (err) {
      throw new Error(getErrorMessageForRequest({ requestName: 'verifyEmailVerificationCode', response: err.response }))
    }
  }

  const handleResend = async () => {
    try {
      return await resendEmailVerification(user.id, verification.id)
    } catch (err) {
      throw new Error(getErrorMessageForRequest({ requestName: 'resendEmailVerification', response: err.response }))
    }
  }

  if (step === 'code') {
    return (
      <EmailVerificationCodeEntry
        email={verification.email}
        title='Verify your new email'
        description="We've sent a code to your new email address, please enter the code to finish updating your Plickers email"
        resendAvailableAt={verification.resendAvailableAt}
        showIllustration={false}
        onSubmitCode={handleVerifyCode}
        onResend={handleResend}
      />
    )
  }

  return (
    <EmailVerificationEmailPasswordForm
      title='Update account email'
      description="We'll send a code to your new email address to complete the change."
      showIllustration={false}
      emailLabel='New email address'
      submitLabel='Send code'
      currentEmail={user.email}
      duplicateEmailError='Please enter a different email than your account email.'
      needsPassword={needsPassword}
      submitLoading={loading}
      errorMessage={formError}
      onSubmit={handleSubmit}
    />
  )
}

export default connect(
  (state) => ({ user: state.user }),
  {
    createEmailVerification, resendEmailVerification, verifyEmailVerification, showNotification,
  },
)(UpdateEmailContainer)
